import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money } from "@/data/site";

export function CheckoutModal() {
  const { checkoutOpen, setCheckoutOpen, cart, subtotal, placeOrder, user } = useApp();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "", city: "", postcode: "", country: "United Kingdom",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && user.email) setForm((f) => ({ ...f, email: f.email || user.email, full_name: f.full_name || user.name }));
  }, [user]);

  if (!checkoutOpen) return null;
  const shipping = subtotal >= 250 ? 0 : 15;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const order = await placeOrder(form);
      navigate(`/checkout/success?order=${order.order_number}`);
    } catch (err) {
      toast.error("Checkout could not be completed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="checkout-modal">
      <div className="absolute inset-0 bg-black/85" onClick={() => setCheckoutOpen(false)} />
      <div className="relative w-full max-w-lg border border-gold/25 bg-ink p-6 sm:p-8 max-h-[90vh] overflow-y-auto reveal">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="overline-label">Delivery Details</p>
            <h3 className="font-serif text-2xl text-cream">Complete your order</h3>
          </div>
          <button onClick={() => setCheckoutOpen(false)} aria-label="Close" data-testid="checkout-close" className="text-cream/70 hover:text-gold"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="checkout-form">
          <Field label="Full name" value={form.full_name} onChange={set("full_name")} testid="co-name" />
          <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="co-email" />
          <Field label="Phone" value={form.phone} onChange={set("phone")} testid="co-phone" />
          <Field label="Address" value={form.address} onChange={set("address")} testid="co-address" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="City" value={form.city} onChange={set("city")} testid="co-city" />
            <Field label="Postcode" value={form.postcode} onChange={set("postcode")} testid="co-postcode" />
          </div>
          <Field label="Country" value={form.country} onChange={set("country")} testid="co-country" />

          <div className="mt-4 space-y-1 border-t border-white/10 pt-4 text-sm">
            <Row label="Subtotal" value={money(subtotal)} />
            <Row label="Shipping" value={shipping === 0 ? "Free" : money(shipping)} />
            <div className="flex justify-between pt-2 text-base"><span className="text-cream">Total</span><span className="font-mono text-gold" data-testid="checkout-total">{money(subtotal + shipping)}</span></div>
          </div>
          <p className="text-xs text-mutedgray">Payment is arranged on confirmation — no card is charged here.</p>
          <button disabled={loading || cart.length === 0} data-testid="place-order-button"
            className="w-full bg-gold py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright disabled:opacity-50">
            {loading ? "Placing order…" : "Place Order"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="overline-label mb-1 block text-[0.6rem]">{label}</span>
      <input required type={type} value={value} onChange={onChange} data-testid={testid}
        className="w-full border border-white/15 bg-black px-4 py-3 text-sm text-cream outline-none transition focus:border-gold" />
    </label>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between text-cream/70"><span>{label}</span><span className="font-mono">{value}</span></div>;
}
